/**
 * Teks halaman tamu (self-order / QR order), terpisah dari kamus utama.
 *
 * Kamus utama (`id.ts` + `en.ts`) ~500 KB untuk aplikasi kasir dan konsol; halaman tamu dibuka di
 * ponsel pelanggan lewat data seluler, jadi ia mengambil berkas kecil ini saja lewat
 * `@ramoo/i18n/tamu`. Kuncinya tetap dua bahasa dengan bentuk yang sama.
 */
const id = {
  /** Judul tab: nama toko yang dibuka tamu di depan, sama dengan kepala halamannya. */
  judulTab: (toko: string) => `${toko} · Pesan`,
  /** L6 Modul 12: consent promo WhatsApp, bawaan tidak dicentang */
  consentPromo: "Kirimi saya promo lewat WhatsApp",
  consentPromoPetunjuk:
    "Khusus usia 18 tahun ke atas. Bisa berhenti kapan saja dengan membalas BERHENTI.",
  /** Modul 13 N6: formulir permintaan data pribadi (UU PDP) */
  privasiTautan: "Privasi & data pribadi",
  privasiJudul: "Permintaan data pribadi",
  privasiSub:
    "Minta salinan, perbaikan, atau penghapusan data pribadimu di toko ini. Permintaan wajib dijawab dalam 3×24 jam.",
  privasiSiapa: "Saya",
  privasiSubjek: { customer: "Pelanggan", staff: "Staf toko" } as Record<string, string>,
  privasiId: "Nomor HP yang terdaftar",
  privasiJenis: "Yang diminta",
  privasiJenisOpsi: {
    access: "Lihat data saya",
    export: "Salinan data saya",
    rectify: "Perbaiki data saya",
    delete: "Hapus data saya",
    withdraw_consent: "Tarik persetujuan promo",
  } as Record<string, string>,
  privasiCatatan: "Keterangan (opsional)",
  privasiKirim: "Kirim permintaan",
  privasiMengirim: "Mengirim…",
  privasiEmail: "Email untuk kabar (opsional)",
  privasiEmailHint:
    "Kami kabari lewat email ini saat permintaanmu diterima, selesai, atau ditolak.",
  privasiStafKeRamoo: "Permintaan staf ditangani Ramoo, karena Ramoo yang memegang data akun staf.",
  privasiTerkirim: (tenggat: string, penjawab: "toko" | "ramoo") =>
    `Permintaanmu sudah diterima. ${penjawab === "ramoo" ? "Ramoo" : "Toko"} akan menjawab paling lambat ${tenggat}.`,
  privasiTokoTakDikenal: "Kode toko ini tidak dikenal. Buka lagi dari QR atau tautan toko.",
  privasiGagal: "Permintaan belum terkirim. Coba lagi sebentar lagi.",
  /** ADR-0017 keputusan rinci 5: menu habis di outlet ini tampil abu-abu, tak bisa dipesan */
  habis: "Habis",
  /* Modul 12 (asesmen UI): teks katalog, lembar pesanan, terkirim + lacak, dan detail produk yang
     dulu ditulis langsung di komponen. */
  meja: (label: string) => `Meja ${label}`,
  mejaAtasNama: (label: string, nama: string) => `Meja ${label} · ${nama}`,
  cariMenu: "Cari menu…",
  cariAria: "Cari menu",
  hapusCari: "Hapus pencarian",
  kategoriAria: "Kategori",
  semua: "Semua",
  lainnya: "Lainnya",
  rekomendasi: "Rekomendasi",
  kosongCari: (q: string) => `Tidak ada menu cocok "${q}".`,
  kosongMenu: "Belum ada menu tersedia.",
  pilihProduk: (nama: string) => `Pilih ${nama}`,
  tambahProduk: (nama: string) => `Tambah ${nama} ke pesanan`,
  barItem: (n: number) => `${n} item`,
  lihatPesanan: (n: number, total: string) => `Lihat pesanan: ${n} item, ${total}`,
  pesanan: "Pesanan",
  namaOpsional: "Nama (opsional)",
  hpOpsional: "No HP — kumpulkan poin (opsional)",
  namaUntukPoin: "Isi nama juga untuk mengumpulkan poin.",
  pesan: (total: string) => `Pesan · ${total}`,
  mengirim: "Mengirim…",
  /** Dulu "Pembayaran di kasir." padahal sesudahnya ada tombol Bayar QRIS. */
  catatanHarga: "Harga final dihitung kasir. Bayar sesudah memesan: QRIS atau di kasir.",
  ulangiTerakhir: "↻ Pesan lagi seperti terakhir",
  terkirim: "Pesanan terkirim",
  nomor: "Nomor",
  poinDipakai: (poin: number, hemat: string) => `${poin} poin dipakai · hemat ${hemat}`,
  bayarQris: "Bayar QRIS sekarang",
  bayarDiKasir: "atau tunjukkan nomor ke kasir untuk membayar",
  isiPesanan: "Isi pesanan",
  pesanLagi: "Pesan lagi",
  perbaruiSendiri:
    "Layar ini memperbarui dirinya sendiri. Kalau ada yang keliru, tunjukkan nomor pesanan di atas ke kasir.",
  statusPesanan: {
    open: "Menunggu pembayaran",
    paid: "Lunas",
    void: "Dibatalkan",
  } as Record<string, string>,
  statusBaris: {
    queued: "Antre",
    preparing: "Sedang disiapkan",
    ready: "Siap",
    served: "Tersaji",
    cancelled: "Dibatalkan",
  } as Record<string, string>,
  memuat: "Memuat…",
  ups: "Ups",
  kembali: "Kembali",
  detailAria: (nama: string) => `Detail ${nama}`,
  tutup: "Tutup",
  varian: "Varian",
  wajib: "Wajib",
  opsional: "Opsional",
  wajibRentang: (min: number, maks: number) => `Wajib · pilih ${min}–${maks}`,
  opsionalMaks: (maks: number) => `Opsional · maks ${maks}`,
  catatan: "Catatan",
  catatanContoh: "Mis. tanpa gula, pedas sedang…",
  tambahTotal: (total: string) => `Tambah · ${total}`,
  /** Tombol Tambah selalu aktif (Modul 12); yang kurang ditandai saat ditekan. */
  pilihDulu: "Pilih dulu",
  maksPesanan: (n: number) => `Maksimal ${n} per pesanan.`,
  kurangi: "Kurangi",
  tambahSatu: "Tambah",
}

export type KamusTamu = typeof id

const en: KamusTamu = {
  judulTab: (toko: string) => `${toko} · Order`,
  consentPromo: "Send me promotions on WhatsApp",
  consentPromoPetunjuk: "Only for ages 18 and over. You can stop anytime by replying STOP.",
  privasiTautan: "Privacy & personal data",
  privasiJudul: "Personal data request",
  privasiSub:
    "Ask for a copy, a correction, or deletion of your personal data at this store. Requests must be answered within 72 hours.",
  privasiSiapa: "I am",
  privasiSubjek: { customer: "A customer", staff: "Store staff" } as Record<string, string>,
  privasiId: "Registered phone number",
  privasiJenis: "What I am asking for",
  privasiJenisOpsi: {
    access: "See my data",
    export: "A copy of my data",
    rectify: "Correct my data",
    delete: "Delete my data",
    withdraw_consent: "Withdraw promo consent",
  } as Record<string, string>,
  privasiCatatan: "Details (optional)",
  privasiKirim: "Send request",
  privasiMengirim: "Sending…",
  privasiEmail: "Email for updates (optional)",
  privasiEmailHint: "We email you here when your request is received, completed, or declined.",
  privasiStafKeRamoo: "Staff requests are handled by Ramoo, which holds staff account data.",
  privasiTerkirim: (tenggat: string, penjawab: "toko" | "ramoo") =>
    `Your request was received. ${penjawab === "ramoo" ? "Ramoo" : "The store"} will answer by ${tenggat} at the latest.`,
  privasiTokoTakDikenal:
    "This store code is not recognised. Open it again from the store's QR or link.",
  privasiGagal: "The request was not sent. Please try again shortly.",
  habis: "Sold out",
  meja: (label: string) => `Table ${label}`,
  mejaAtasNama: (label: string, nama: string) => `Table ${label} · ${nama}`,
  cariMenu: "Search the menu…",
  cariAria: "Search the menu",
  hapusCari: "Clear search",
  kategoriAria: "Category",
  semua: "All",
  lainnya: "Other",
  rekomendasi: "Recommended",
  kosongCari: (q: string) => `No menu items match "${q}".`,
  kosongMenu: "No menu items yet.",
  pilihProduk: (nama: string) => `Choose ${nama}`,
  tambahProduk: (nama: string) => `Add ${nama} to the order`,
  barItem: (n: number) => `${n} items`,
  lihatPesanan: (n: number, total: string) => `View order: ${n} items, ${total}`,
  pesanan: "Order",
  namaOpsional: "Name (optional)",
  hpOpsional: "Phone — collect points (optional)",
  namaUntukPoin: "Add your name too to collect points.",
  pesan: (total: string) => `Order · ${total}`,
  mengirim: "Sending…",
  catatanHarga:
    "The cashier calculates the final price. Pay after ordering: QRIS or at the cashier.",
  ulangiTerakhir: "↻ Order the same as last time",
  terkirim: "Order sent",
  nomor: "Number",
  poinDipakai: (poin: number, hemat: string) => `${poin} points used · saved ${hemat}`,
  bayarQris: "Pay with QRIS now",
  bayarDiKasir: "or show the number at the cashier to pay",
  isiPesanan: "Your order",
  pesanLagi: "Order again",
  perbaruiSendiri:
    "This screen updates itself. If something is wrong, show the order number above to the cashier.",
  statusPesanan: {
    open: "Waiting for payment",
    paid: "Paid",
    void: "Cancelled",
  } as Record<string, string>,
  statusBaris: {
    queued: "Queued",
    preparing: "Being prepared",
    ready: "Ready",
    served: "Served",
    cancelled: "Cancelled",
  } as Record<string, string>,
  memuat: "Loading…",
  ups: "Oops",
  kembali: "Back",
  detailAria: (nama: string) => `${nama} details`,
  tutup: "Close",
  varian: "Variant",
  wajib: "Required",
  opsional: "Optional",
  wajibRentang: (min: number, maks: number) => `Required · choose ${min}–${maks}`,
  opsionalMaks: (maks: number) => `Optional · up to ${maks}`,
  catatan: "Note",
  catatanContoh: "E.g. no sugar, medium spicy…",
  tambahTotal: (total: string) => `Add · ${total}`,
  pilihDulu: "Choose first",
  maksPesanan: (n: number) => `Up to ${n} per order.`,
  kurangi: "Decrease",
  tambahSatu: "Increase",
}

export const kamusTamu = { id, en } as const
export const tamu = (locale: "id" | "en" = "id"): KamusTamu => kamusTamu[locale]
